import { useCallback, useRef } from 'react'
import useFetch, { UseFetch, CachePolicies, ResponseType } from 'use-http'
import { addSuperAdminHeaders } from 'utils/superAdmin/superAdminUtils'
import useThriveAuth from './useThriveAuth'

// Define a single options object interface
interface UseApiOptions {
  endpoint?: string | null
  dependencies?: null
  cachePolicy?: CachePolicies
  redirectOnUnauthorized?: boolean
  headers?: Record<string, string>
  // Overrides use-http's default ['json', 'text', 'blob', 'arrayBuffer']
  // response parsing. Use e.g. 'text' for non-JSON endpoints (CSV downloads).
  responseType?: ResponseType
}

export type UseApi<TData> = UseFetch<TData> & {
  /**
   * How many of this instance's requests were aborted so far. use-http
   * swallows an abort: the call resolves with undefined or the instance's
   * previous payload, and `request.error` stays unset. Compare the count
   * before and after a call to tell an abort from a real response.
   *
   * Always set by useApi. Optional only so test mocks typed as plain
   * `UseFetch` still fit.
   */
  getAbortCount?: () => number
}

// Type any is intentional here

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useApi = <TData = any>(options: UseApiOptions = {}): UseApi<TData> => {
  const {
    endpoint = null,
    dependencies = null,
    cachePolicy = CachePolicies.NETWORK_ONLY,
    redirectOnUnauthorized = true,
    headers = {},
    responseType,
  } = options

  const { getAccessTokenSilently, loginWithRedirect, logout } = useThriveAuth()

  // Stable callbacks: use-http rebuilds `request` when onAbort changes.
  const abortCount = useRef(0)
  const onAbort = useCallback(() => {
    abortCount.current += 1
  }, [])
  const getAbortCount = useCallback(() => abortCount.current, [])

  const requestConfig = {
    cachePolicy,
    onAbort,
    // use-http only auto-adds a Content-Type for POST/PUT/PATCH bodies
    // (see doFetchArgs's shouldAddContentType), so a DELETE with a JSON body
    // falls back to the browser's text/plain default unless the caller's
    // `headers` are set here too, not just merged in by the interceptor below.
    headers,
    // Only override when explicitly provided so every other caller keeps
    // use-http's default json/text/blob/arrayBuffer fallback behavior.
    ...(responseType ? { responseType } : {}),
    interceptors: {
      request: async ({ options }) => {
        try {
          const token = getAccessTokenSilently()

          // Don't send "Bearer null" when storage is cleared/expired — omit the
          // header so the request fails closed (401 → the response interceptor
          // below redirects to login) instead of leaking a request with junk auth.
          if (token) {
            options.headers.Authorization = `Bearer ${token}`
          }

          options = addSuperAdminHeaders(options)
          options.headers = { ...options.headers, ...headers }
        } catch (error) {
          // ignore
        }

        return options
      },
      response: ({ response }) => {
        if (response.status === 401) {
          if (redirectOnUnauthorized) {
            loginWithRedirect({ clearCredentials: true })
          } else {
            // Clear credentials without redirecting - for public/SEO pages
            logout({})
          }
        }

        return response
      },
    },
  }

  const serverUrl = `${process.env.NEXT_PUBLIC_SERVER_URL}${endpoint || ''}`

  return Object.assign(
    useFetch<TData>(serverUrl, requestConfig, dependencies),
    {
      getAbortCount,
    }
  )
}

export default useApi
